import React, { useMemo } from 'react'
import type {
  OverlapEnrollment,
  OverlapPair,
} from '../api/enrollment-overlap.types'
import { buildTimeline } from '../utils/timeline'
import { formatDate } from '../utils/run-display'

interface OverlapTimelineProps {
  enrollments: OverlapEnrollment[]
  overlaps: OverlapPair[]
}

const TRACK_STYLE: React.CSSProperties = {
  position: 'relative',
  flex: 1,
  height: 16,
  background: '#f5f5f5',
  borderRadius: 4,
}

/**
 * A visual aid only: the enrollment and overlap tables carry the same
 * information in an accessible form, so the bars are hidden from assistive
 * technology.
 */
export default function OverlapTimeline({
  enrollments,
  overlaps,
}: OverlapTimelineProps) {
  const timeline = useMemo(
    () => buildTimeline(enrollments, overlaps),
    [enrollments, overlaps]
  )

  if (timeline.axisStart === null || timeline.axisEnd === null) return null

  return (
    <div aria-hidden="true" data-testid="overlap-timeline">
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          marginLeft: 220,
          fontSize: 12,
          color: '#595959',
          marginBottom: 4,
        }}
      >
        <span>{formatDate(timeline.axisStart)}</span>
        <span>{formatDate(timeline.axisEnd)}</span>
      </div>
      {timeline.rows.map((row) => (
        <div
          key={row.enrollmentId}
          style={{ display: 'flex', alignItems: 'center', marginBottom: 8 }}
        >
          <div
            title={row.label}
            style={{
              width: 220,
              paddingRight: 12,
              fontSize: 12,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {row.label}
          </div>
          <div style={TRACK_STYLE}>
            {row.bar ? (
              <div
                style={{
                  position: 'absolute',
                  top: 0,
                  bottom: 0,
                  left: `${row.bar.left}%`,
                  width: `${row.bar.width}%`,
                  background: '#91caff',
                  borderRadius: 4,
                }}
              />
            ) : null}
            {row.overlaps.map((window, index) => (
              <div
                key={index}
                style={{
                  position: 'absolute',
                  top: 0,
                  bottom: 0,
                  left: `${window.left}%`,
                  width: `${window.width}%`,
                  background: '#ff4d4f',
                  opacity: 0.85,
                  borderRadius: 4,
                }}
              />
            ))}
          </div>
        </div>
      ))}
      <div style={{ fontSize: 12, color: '#595959', marginTop: 4 }}>
        <span style={{ color: '#1677ff' }}>■</span> Enrollment period{' '}
        <span style={{ color: '#ff4d4f', marginLeft: 12 }}>■</span> Overlapping
        window
      </div>
    </div>
  )
}
